import { For, Show, createEffect, createSignal, onCleanup } from 'solid-js';
import Text from '../Text';
import SchematicPreview from './SchematicPreview';
import type { PreviewMesh } from '../../mapart/previewMesh';
import type { BuildResult } from '../../mapart/types';
import { type Viewport, pan, toArtwork, toScreen, zoomAt } from '../../mapart/viewport';
import styles from './MapartCanvas.module.css';

export type CanvasView = 'image' | 'map' | 'both' | '3d';

export type ResizeHandle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

export interface SelectionBox {
    x: number;
    y: number;
    width: number;
    height: number;
    rotation: number;
    /**
     * Shapes resize freely; images and text keep their aspect, so only corners are offered. A group
     * box offers none, since one box cannot express a resize across several layers.
     */
    handles: 'free' | 'uniform' | 'none';
}

/** Outline per layer of a group selection, so the union box is not the only thing on screen. */
export interface SelectionMember {
    x: number;
    y: number;
    width: number;
    height: number;
    rotation: number;
}

const ALL_HANDLES: ResizeHandle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
const CORNER_HANDLES: ResizeHandle[] = ['nw', 'ne', 'se', 'sw'];

const handlesFor = (mode: SelectionBox['handles'] | undefined): ResizeHandle[] =>
    mode === 'free' ? ALL_HANDLES : mode === 'uniform' ? CORNER_HANDLES : [];

const HANDLE_POSITION: Record<ResizeHandle, { left: string; top: string; cursor: string }> = {
    nw: { left: '0%', top: '0%', cursor: 'nwse-resize' },
    n: { left: '50%', top: '0%', cursor: 'ns-resize' },
    ne: { left: '100%', top: '0%', cursor: 'nesw-resize' },
    e: { left: '100%', top: '50%', cursor: 'ew-resize' },
    se: { left: '100%', top: '100%', cursor: 'nwse-resize' },
    s: { left: '50%', top: '100%', cursor: 'ns-resize' },
    sw: { left: '0%', top: '100%', cursor: 'nesw-resize' },
    w: { left: '0%', top: '50%', cursor: 'ew-resize' },
};

/** Gentle enough that one trackpad flick does not shoot past the artwork. */
const ZOOM_PER_WHEEL_UNIT = 0.0015;

/** Floor for the filled height, so a cramped window still leaves something to work on. */
const MIN_SURFACE_HEIGHT = 240;
/** Wheels reporting lines rather than pixels send about three per notch. */
const LINE_HEIGHT = 16;

// Raw canvases on purpose: no shared component renders pixel data, and the map preview has to paint
// the palette bytes 1:1 with pixelated scaling rather than go through an image element.
interface Props {
    composite: HTMLCanvasElement;
    result: BuildResult | null;
    width: number;
    length: number;
    /** Where the artwork sits inside the surface and how far it is magnified. */
    viewport: Viewport;
    view: CanvasView;
    /** Only read in the 3D view, where it replaces both flat panes. */
    mesh: PreviewMesh | null;
    busy: boolean;
    mapsX: number;
    mapsZ: number;
    showGrid: boolean;
    /** Bumped once the page has drawn the current document into `composite`. */
    compositeRevision: number;
    selection: SelectionBox | null;
    /** Empty unless several layers are selected. */
    members: SelectionMember[];
    guides: { x: number | null; y: number | null };
    /** Shift or Ctrl held means add to or remove from the selection instead of replacing it. Returns
     * whether a layer was grabbed, since a press on empty canvas pans the view instead. */
    onPick: (x: number, y: number, additive: boolean) => boolean;
    /** Shift held means the caller should skip alignment snapping. */
    onDrag: (dx: number, dy: number, disableSnap: boolean) => void;
    /** `moved` false means it was a click, which the caller may treat as a plain selection. */
    onDragEnd: (moved: boolean) => void;
    /** Deltas are in the layer's own axes, so a rotated layer resizes along its edges. */
    onResize: (handle: ResizeHandle, dx: number, dy: number) => void;
    onViewport: (next: Viewport) => void;
    /** Lets the page fit and zoom about the centre without measuring the surface itself. */
    onSurfaceSize: (width: number, height: number) => void;
}

export default function MapartCanvas(props: Props) {
    // Signals rather than plain refs: switching the view remounts these canvases, and the paint
    // effects must re-run for the new element instead of leaving it blank until the next change.
    const [imageCanvas, setImageCanvas] = createSignal<HTMLCanvasElement>();
    const [mapCanvas, setMapCanvas] = createSignal<HTMLCanvasElement>();
    const [imageSurface, setImageSurface] = createSignal<HTMLDivElement>();
    const [mapSurface, setMapSurface] = createSignal<HTMLDivElement>();
    const [previewSurface, setPreviewSurface] = createSignal<HTMLDivElement>();
    const [stack, setStack] = createSignal<HTMLDivElement>();
    let mode: 'none' | 'layer' | 'pan' = 'none';
    let moved = false;
    let lastX = 0;
    let lastY = 0;

    createEffect(() => {
        props.compositeRevision;
        const canvas = imageCanvas();
        if (!canvas) return;
        canvas.width = props.width;
        canvas.height = props.length;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.clearRect(0, 0, props.width, props.length);
        ctx.drawImage(props.composite, 0, 0);
    });

    createEffect(() => {
        const result = props.result;
        const canvas = mapCanvas();
        if (!canvas) return;
        canvas.width = props.width;
        canvas.height = props.length;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.clearRect(0, 0, props.width, props.length);
        if (!result) return;
        const pixels = new Uint8ClampedArray(result.previewRgba);
        ctx.putImageData(new ImageData(pixels, result.width, result.length), 0, 0);
    });

    /** The dashboard scrolls in its own element, so the window is not what the canvas has to fit. */
    const scrollParent = (element: HTMLElement): HTMLElement => {
        for (let node = element.parentElement; node; node = node.parentElement) {
            const overflow = getComputedStyle(node).overflowY;
            if (overflow === 'auto' || overflow === 'scroll') return node;
        }
        return document.documentElement;
    };

    /**
     * What sits between the canvas and the bottom of the scroll area: the card's padding, the hint
     * line under it, the page padding. A grid step contributes nothing, since the canvas shares that
     * row with the side panels and a taller panel is beside the canvas, not below it.
     */
    const spaceBelow = (from: HTMLElement, scroller: HTMLElement) => {
        // The scroller's own rectangle is the visible window rather than its content, so only its
        // padding counts here.
        let total = Number.parseFloat(getComputedStyle(scroller).paddingBottom) || 0;
        for (let node = from; node.parentElement && node.parentElement !== scroller; node = node.parentElement) {
            const style = getComputedStyle(node.parentElement);
            // `display: contents` leaves no box to measure at all.
            if (style.display === 'contents') continue;
            if (style.display === 'grid' && style.gridTemplateColumns.split(' ').length > 1) continue;
            total += node.parentElement.getBoundingClientRect().bottom - node.getBoundingClientRect().bottom;
        }
        return total;
    };

    /**
     * The surfaces take whatever height is left over: what sits above and below the canvas keeps the
     * size it asked for, and the rest is the viewport. Measured in the scroll container's own
     * coordinates, so the result does not depend on where the page happens to be scrolled, and it does
     * not move when the height it produces is applied.
     */
    /** Only the panes actually on screen: switching the view leaves the other signal holding a
     * detached element, and a detached element measures as zero. */
    const livePanes = () =>
        [imageSurface(), mapSurface(), previewSurface()].filter((surface): surface is HTMLDivElement => !!surface?.isConnected);

    const stickyAncestor = (from: HTMLElement, scroller: HTMLElement) => {
        for (let node = from.parentElement; node && node !== scroller; node = node.parentElement) {
            if (getComputedStyle(node).position === 'sticky') return node;
        }
        return null;
    };

    const fillHeight = () => {
        const container = stack();
        const panes = livePanes();
        if (!container || panes.length === 0) return;
        const scroller = scrollParent(container);
        const origin = scroller === document.documentElement ? 0 : scroller.getBoundingClientRect().top;
        // A stuck ancestor reports where it is stuck, and the height has to come from where it sits in
        // the page, or every edit made while scrolled down would resize the canvas.
        const sticky = stickyAncestor(container, scroller);
        const stickyPosition = sticky?.style.position ?? '';
        if (sticky) sticky.style.position = 'static';
        const above = container.getBoundingClientRect().top - origin + scroller.scrollTop;
        if (sticky) sticky.style.position = stickyPosition;
        const free = scroller.clientHeight - above - spaceBelow(container, scroller);

        // Panes wrap into rows when the column is narrow, and each row needs a share of the space.
        const wrapper = (surface: HTMLDivElement) => surface.parentElement as HTMLElement;
        const rows = new Set(panes.map((surface) => wrapper(surface).offsetTop)).size;
        const caption = wrapper(panes[0]).clientHeight - panes[0].clientHeight;
        const rowGap = Number.parseFloat(getComputedStyle(container).rowGap) || 0;
        const height = (free - rows * caption - (rows - 1) * rowGap) / rows;
        const next = `${Math.max(MIN_SURFACE_HEIGHT, Math.round(height))}px`;
        // Only on a real change, so measuring on every edit cannot turn into a write every frame.
        if (container.style.getPropertyValue('--surface-height') !== next) {
            container.style.setProperty('--surface-height', next);
        }
    };

    createEffect(() => {
        // Switching panes changes what sits around the canvas, so the free space is measured again.
        props.view;
        const [element] = livePanes();
        if (!element) return;
        fillHeight();
        // Two triggers on purpose. The event covers the window changing size, including the cases
        // where a hidden or background tab never delivers observer callbacks; the observer covers the
        // sidebars opening and the toolbar rewrapping, which resize nothing but the scroll area. The
        // scroll area's own box does not change when the surface height does, so this cannot chase
        // itself.
        window.addEventListener('resize', fillHeight);
        const observer = new ResizeObserver(fillHeight);
        observer.observe(scrollParent(element));
        onCleanup(() => {
            window.removeEventListener('resize', fillHeight);
            observer.disconnect();
        });
    });

    // What sits above the canvas moves with the document, the drop hint being the clearest case, so
    // the free space is worth measuring again whenever the artwork is redrawn.
    createEffect(() => {
        props.compositeRevision;
        fillHeight();
    });

    // Both flat surfaces share a size, so whichever one is on screen can report for the pair.
    createEffect(() => {
        if (props.view === '3d') return;
        const element = props.view === 'map' ? mapSurface() : imageSurface();
        if (!element) return;
        const observer = new ResizeObserver(() => props.onSurfaceSize(element.clientWidth, element.clientHeight));
        observer.observe(element);
        props.onSurfaceSize(element.clientWidth, element.clientHeight);
        onCleanup(() => observer.disconnect());
    });

    const surfacePoint = (event: PointerEvent | WheelEvent, target: HTMLElement) => {
        const rect = target.getBoundingClientRect();
        return { x: event.clientX - rect.left, y: event.clientY - rect.top };
    };

    const onPointerDown = (event: PointerEvent) => {
        // Without this the browser starts its own text selection drag and the move stream stops.
        event.preventDefault();
        const surface = event.currentTarget as HTMLElement;
        surface.setPointerCapture(event.pointerId);
        const screen = surfacePoint(event, surface);
        moved = false;
        // The middle button always pans. The left button pans too when it grabbed no layer, so
        // dragging empty canvas moves the view rather than doing nothing.
        if (event.button === 1) {
            mode = 'pan';
        } else {
            const point = toArtwork(props.viewport, screen.x, screen.y);
            const additive = event.shiftKey || event.ctrlKey || event.metaKey;
            mode = props.onPick(point.x, point.y, additive) ? 'layer' : 'pan';
        }
        if (mode === 'pan') {
            lastX = screen.x;
            lastY = screen.y;
        } else {
            const point = toArtwork(props.viewport, screen.x, screen.y);
            lastX = point.x;
            lastY = point.y;
        }
    };

    const onPointerMove = (event: PointerEvent) => {
        if (mode === 'none') return;
        const screen = surfacePoint(event, event.currentTarget as HTMLElement);
        if (mode === 'pan') {
            const dx = screen.x - lastX;
            const dy = screen.y - lastY;
            if (dx === 0 && dy === 0) return;
            lastX = screen.x;
            lastY = screen.y;
            props.onViewport(pan(props.viewport, dx, dy));
            return;
        }
        const point = toArtwork(props.viewport, screen.x, screen.y);
        if (point.x !== lastX || point.y !== lastY) moved = true;
        props.onDrag(point.x - lastX, point.y - lastY, event.shiftKey);
        lastX = point.x;
        lastY = point.y;
    };

    const stopDrag = () => {
        if (mode === 'none') return;
        const wasLayer = mode === 'layer';
        mode = 'none';
        if (wasLayer) props.onDragEnd(moved);
    };

    const onWheel = (event: WheelEvent) => {
        // The page must not scroll away under the cursor while the view is being zoomed.
        event.preventDefault();
        const screen = surfacePoint(event, event.currentTarget as HTMLElement);
        const distance = event.deltaY * (event.deltaMode === 1 ? LINE_HEIGHT : 1);
        props.onViewport(zoomAt(props.viewport, Math.exp(-distance * ZOOM_PER_WHEEL_UNIT), screen.x, screen.y));
    };

    const startResize = (handle: ResizeHandle, event: PointerEvent) => {
        event.preventDefault();
        event.stopPropagation();
        const target = event.currentTarget as HTMLElement;
        target.setPointerCapture(event.pointerId);
        let lastX = event.clientX;
        let lastY = event.clientY;

        const move = (moveEvent: PointerEvent) => {
            const radians = (-(props.selection?.rotation ?? 0) * Math.PI) / 180;
            const dx = (moveEvent.clientX - lastX) / props.viewport.zoom;
            const dy = (moveEvent.clientY - lastY) / props.viewport.zoom;
            lastX = moveEvent.clientX;
            lastY = moveEvent.clientY;
            props.onResize(
                handle,
                dx * Math.cos(radians) - dy * Math.sin(radians),
                dx * Math.sin(radians) + dy * Math.cos(radians),
            );
        };

        const finish = () => {
            target.removeEventListener('pointermove', move);
            target.removeEventListener('pointerup', finish);
            target.removeEventListener('pointercancel', finish);
            props.onDragEnd(true);
        };

        target.addEventListener('pointermove', move);
        target.addEventListener('pointerup', finish);
        target.addEventListener('pointercancel', finish);
    };

    onCleanup(stopDrag);

    const zoom = () => props.viewport.zoom;

    /** Artwork layer, placed inside the surface by the viewport, so the surface itself can clip. */
    const contentStyle = () => {
        const origin = toScreen(props.viewport, 0, 0);
        return {
            left: `${origin.x}px`,
            top: `${origin.y}px`,
            width: `${props.width * zoom()}px`,
            height: `${props.length * zoom()}px`,
        };
    };

    const gridStyle = () => ({
        'background-size': `${100 / props.mapsX}% ${100 / props.mapsZ}%`,
    });

    return (
        <div ref={setStack} class={styles.stack}>
            <Show when={props.view === 'image' || props.view === 'both'}>
                <div class={`v ${styles.pane}`}>
                    <span class={styles.caption}><Text val="MAPART_VIEW_IMAGE" /></span>
                    <div
                        ref={setImageSurface}
                        class={styles.surface}
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={stopDrag}
                        onPointerCancel={stopDrag}
                        onWheel={onWheel}
                    >
                        <div class={styles.content} style={contentStyle()}>
                            <canvas ref={setImageCanvas} class={styles.canvas} />
                            <Show when={props.showGrid}>
                                <div class={styles.grid} style={gridStyle()} />
                            </Show>
                            <Show when={props.guides.x !== null}>
                                <div class={styles.guideX} style={{ left: `${(props.guides.x ?? 0) * zoom()}px` }} />
                            </Show>
                            <Show when={props.guides.y !== null}>
                                <div class={styles.guideY} style={{ top: `${(props.guides.y ?? 0) * zoom()}px` }} />
                            </Show>
                            <For each={props.members}>{(member) => (
                                <div
                                    class={styles.member}
                                    style={{
                                        left: `${member.x * zoom()}px`,
                                        top: `${member.y * zoom()}px`,
                                        width: `${Math.max(1, member.width * zoom())}px`,
                                        height: `${Math.max(1, member.height * zoom())}px`,
                                        transform: `rotate(${member.rotation}deg)`,
                                    }}
                                />
                            )}</For>
                            {/* Not keyed: a keyed Show would rebuild the handles mid drag and drop the capture. */}
                            <Show when={props.selection}>
                                <div
                                    class={styles.selection}
                                    style={{
                                        left: `${(props.selection?.x ?? 0) * zoom()}px`,
                                        top: `${(props.selection?.y ?? 0) * zoom()}px`,
                                        width: `${Math.max(1, (props.selection?.width ?? 0) * zoom())}px`,
                                        height: `${Math.max(1, (props.selection?.height ?? 0) * zoom())}px`,
                                        transform: `rotate(${props.selection?.rotation ?? 0}deg)`,
                                    }}
                                >
                                    <For each={handlesFor(props.selection?.handles)}>
                                        {(handle) => (
                                            <span
                                                class={styles.handle}
                                                style={{
                                                    left: HANDLE_POSITION[handle].left,
                                                    top: HANDLE_POSITION[handle].top,
                                                    cursor: HANDLE_POSITION[handle].cursor,
                                                }}
                                                onPointerDown={(e) => startResize(handle, e)}
                                            />
                                        )}
                                    </For>
                                </div>
                            </Show>
                        </div>
                    </div>
                </div>
            </Show>

            <Show when={props.view === '3d'}>
                <div class={`v ${styles.pane}`}>
                    <span class={styles.caption}><Text val="MAPART_VIEW_3D" /></span>
                    <div ref={setPreviewSurface} class={styles.surface}>
                        <SchematicPreview mesh={props.mesh} busy={props.busy} />
                    </div>
                </div>
            </Show>

            <Show when={props.view === 'map' || props.view === 'both'}>
                <div class={`v ${styles.pane}`}>
                    <span class={styles.caption}><Text val="MAPART_VIEW_MAP" /></span>
                    <div
                        ref={setMapSurface}
                        class={styles.surface}
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={stopDrag}
                        onPointerCancel={stopDrag}
                        onWheel={onWheel}
                    >
                        <div class={styles.content} style={contentStyle()}>
                            <canvas ref={setMapCanvas} class={styles.canvas} />
                            <Show when={props.showGrid}>
                                <div class={styles.grid} style={gridStyle()} />
                            </Show>
                        </div>
                    </div>
                </div>
            </Show>
        </div>
    );
}
